import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { ArrowLeft, Plus, X, Trash2, Boxes, Pencil } from "lucide-react";

const FIELD_TYPE_LABEL = { text: "Text", number: "Number", date: "Date", select: "Dropdown", checkbox: "Checkbox", url: "URL" };

const EMPTY_FIELD_FORM = { label: "", field_type: "text", options: "", is_required: false, is_unique: false };

function valueInput(field, value, onChange) {
  const ftype = field.field_type;
  if (ftype === "checkbox") {
    return (
      <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)}
        className="w-4 h-4" />
    );
  }
  if (ftype === "select") {
    return (
      <select value={value ?? ""} onChange={e => onChange(e.target.value)}
        className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
        <option value="">—</option>
        {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (ftype === "number") {
    return (
      <input type="number" step="any" value={value ?? ""} onChange={e => onChange(e.target.value)}
        className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
    );
  }
  if (ftype === "date") {
    return (
      <input type="date" value={value ?? ""} onChange={e => onChange(e.target.value)}
        className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
    );
  }
  if (ftype === "url") {
    return (
      <input type="url" value={value ?? ""} onChange={e => onChange(e.target.value)} placeholder="https://…"
        className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
    );
  }
  return (
    <input type="text" value={value ?? ""} onChange={e => onChange(e.target.value)}
      className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
  );
}

function displayValue(field, value) {
  if (value === undefined || value === null || value === "") return "—";
  if (field.field_type === "checkbox") return value ? "Yes" : "No";
  return String(value);
}

export default function CustomModuleDetail() {
  const { moduleId } = useParams();
  const [tab, setTab] = useState("records");
  const [module_, setModule] = useState(null);
  const [fields, setFields] = useState([]);
  const [records, setRecords] = useState([]);

  const [showFieldModal, setShowFieldModal] = useState(false);
  const [fieldForm, setFieldForm] = useState(EMPTY_FIELD_FORM);
  const [savingField, setSavingField] = useState(false);

  const [showRecordModal, setShowRecordModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [recordTitle, setRecordTitle] = useState("");
  const [recordValues, setRecordValues] = useState({});
  const [savingRecord, setSavingRecord] = useState(false);

  const loadModule = useCallback(async () => {
    try {
      const { data } = await api.get("/custom-modules");
      setModule((data || []).find(m => m.module_id === moduleId) || null);
    } catch {
      toast.error("Failed to load module");
    }
  }, [moduleId]);

  const loadFields = useCallback(async () => {
    try {
      const { data } = await api.get(`/custom-fields/definitions?entity_type=module:${moduleId}&include_inactive=true`);
      setFields(data || []);
    } catch {
      toast.error("Failed to load fields");
    }
  }, [moduleId]);

  const loadRecords = useCallback(async () => {
    try {
      const { data } = await api.get(`/custom-modules/${moduleId}/records`);
      setRecords(data || []);
    } catch {
      toast.error("Failed to load records");
    }
  }, [moduleId]);

  useEffect(() => { loadModule(); loadFields(); loadRecords(); }, [loadModule, loadFields, loadRecords]);

  const activeFields = fields.filter(f => f.is_active);

  // ── Fields tab ──────────────────────────────────────────────────────────
  const createField = async () => {
    if (!fieldForm.label.trim()) return toast.error("Label is required");
    const options = fieldForm.field_type === "select"
      ? fieldForm.options.split("\n").map(o => o.trim()).filter(Boolean)
      : [];
    if (fieldForm.field_type === "select" && options.length === 0) return toast.error("Add at least one option, one per line");
    setSavingField(true);
    try {
      await api.post("/custom-fields/definitions", {
        entity_type: `module:${moduleId}`,
        label: fieldForm.label,
        field_type: fieldForm.field_type,
        options,
        is_required: fieldForm.is_required,
        is_unique: fieldForm.is_unique,
      });
      toast.success("Field created");
      setShowFieldModal(false);
      setFieldForm(EMPTY_FIELD_FORM);
      loadFields();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create field");
    } finally {
      setSavingField(false);
    }
  };

  const toggleFieldActive = async (f) => {
    try {
      await api.put(`/custom-fields/definitions/${f.field_id}`, { is_active: !f.is_active });
      loadFields();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update field");
    }
  };

  const removeField = async (f) => {
    if (!window.confirm(`Delete field "${f.label}"? Any values already saved for it will be removed too.`)) return;
    try {
      await api.delete(`/custom-fields/definitions/${f.field_id}`);
      toast.success("Field deleted");
      loadFields();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete field");
    }
  };

  // ── Records tab ─────────────────────────────────────────────────────────
  const openNewRecord = () => {
    setEditingRecord(null);
    setRecordTitle("");
    setRecordValues({});
    setShowRecordModal(true);
  };

  const openEditRecord = (r) => {
    setEditingRecord(r);
    setRecordTitle(r.title);
    setRecordValues({ ...(r.values || {}) });
    setShowRecordModal(true);
  };

  const saveRecord = async () => {
    if (!recordTitle.trim()) return toast.error("Title is required");
    setSavingRecord(true);
    try {
      if (editingRecord) {
        await api.put(`/custom-modules/${moduleId}/records/${editingRecord.record_id}`, {
          title: recordTitle,
          values: recordValues,
        });
        toast.success("Record updated");
      } else {
        await api.post(`/custom-modules/${moduleId}/records`, {
          title: recordTitle,
          values: recordValues,
        });
        toast.success("Record created");
      }
      setShowRecordModal(false);
      loadRecords();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save record");
    } finally {
      setSavingRecord(false);
    }
  };

  const removeRecord = async (r) => {
    if (!window.confirm(`Delete record "${r.title}"?`)) return;
    try {
      await api.delete(`/custom-modules/${moduleId}/records/${r.record_id}`);
      toast.success("Record deleted");
      loadRecords();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete record");
    }
  };

  return (
    <DarkPage><div className="p-6 max-w-5xl mx-auto">
      <Link to="/custom-modules" className="text-xs text-[#7d8590] hover:text-white flex items-center gap-1 mb-3 w-fit">
        <ArrowLeft className="w-3.5 h-3.5" /> All modules
      </Link>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Boxes className="w-5 h-5 text-[#7d8590]" /> {module_?.name || "…"}
        </h1>
      </div>

      <div className="flex bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden w-fit mb-6">
        {[{ key: "records", label: "Records" }, { key: "fields", label: "Fields" }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm transition-colors ${tab === t.key ? "bg-[#1c2128] text-white font-medium" : "text-[#7d8590] hover:text-white"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "fields" && (
        <>
          <div className="flex justify-end mb-4">
            <Button onClick={() => { setFieldForm(EMPTY_FIELD_FORM); setShowFieldModal(true); }} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
              <Plus className="w-4 h-4 mr-1" /> New Field
            </Button>
          </div>
          {fields.length === 0 ? (
            <div className="text-center py-16 text-[#7d8590] text-sm">No fields defined yet. Add one to start capturing structured data.</div>
          ) : (
            <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#21262d]">
                    {["Label", "Key", "Type", "Required", "Unique", "Status", ""].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs text-[#7d8590] font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fields.map(f => (
                    <tr key={f.field_id} className="border-b border-[#21262d] last:border-0 group">
                      <td className="px-4 py-3 text-white">{f.label}</td>
                      <td className="px-4 py-3 text-[#7d8590] font-mono text-xs">{f.key}</td>
                      <td className="px-4 py-3 text-[#7d8590]">{FIELD_TYPE_LABEL[f.field_type]}{f.field_type === "select" ? ` (${(f.options || []).length})` : ""}</td>
                      <td className="px-4 py-3 text-[#7d8590]">{f.is_required ? "Yes" : "—"}</td>
                      <td className="px-4 py-3 text-[#7d8590]">{f.is_unique ? "Yes" : "—"}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => toggleFieldActive(f)}
                          className={`text-xs px-2 py-0.5 rounded-full border ${f.is_active ? "text-[#00F5D4] bg-[#00F5D4]/10 border-[#00F5D4]/20" : "text-[#7d8590] bg-[#1c2128] border-[#30363d]"}`}>
                          {f.is_active ? "active" : "inactive"}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => removeField(f)} className="opacity-0 group-hover:opacity-100 text-[#484f58] hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "records" && (
        <>
          <div className="flex justify-end mb-4">
            <Button onClick={openNewRecord} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
              <Plus className="w-4 h-4 mr-1" /> New Record
            </Button>
          </div>
          {records.length === 0 ? (
            <div className="text-center py-16 text-[#7d8590] text-sm">No records yet.</div>
          ) : (
            <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#21262d]">
                    <th className="text-left px-4 py-3 text-xs text-[#7d8590] font-medium">Title</th>
                    {activeFields.map(f => (
                      <th key={f.field_id} className="text-left px-4 py-3 text-xs text-[#7d8590] font-medium whitespace-nowrap">{f.label}</th>
                    ))}
                    <th className="text-left px-4 py-3 text-xs text-[#7d8590] font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {records.map(r => (
                    <tr key={r.record_id} className="border-b border-[#21262d] last:border-0 group">
                      <td className="px-4 py-3 text-white whitespace-nowrap">{r.title}</td>
                      {activeFields.map(f => (
                        <td key={f.field_id} className="px-4 py-3 text-[#7d8590] whitespace-nowrap">{displayValue(f, (r.values || {})[f.field_id])}</td>
                      ))}
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button onClick={() => openEditRecord(r)} className="opacity-0 group-hover:opacity-100 p-1.5 text-[#7d8590] hover:text-white"><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => removeRecord(r)} className="opacity-0 group-hover:opacity-100 p-1.5 text-[#7d8590] hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showFieldModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 overflow-y-auto py-8">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">New field</h2>
              <button onClick={() => setShowFieldModal(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Label *</label>
                <input value={fieldForm.label} onChange={e => setFieldForm(f => ({ ...f, label: e.target.value }))}
                  placeholder="e.g. Contact Email"
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Type</label>
                <select value={fieldForm.field_type} onChange={e => setFieldForm(f => ({ ...f, field_type: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                  {Object.entries(FIELD_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              {fieldForm.field_type === "select" && (
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Options (one per line)</label>
                  <textarea value={fieldForm.options} onChange={e => setFieldForm(f => ({ ...f, options: e.target.value }))} rows={4}
                    placeholder={"EMEA\nAPAC\nAmericas"}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm resize-none font-mono" />
                </div>
              )}
              <label className="flex items-center gap-2 text-xs text-[#7d8590]">
                <input type="checkbox" checked={fieldForm.is_required} onChange={e => setFieldForm(f => ({ ...f, is_required: e.target.checked }))} />
                Mark as required (shown with an asterisk)
              </label>
              <label className="flex items-center gap-2 text-xs text-[#7d8590]">
                <input type="checkbox" checked={fieldForm.is_unique} onChange={e => setFieldForm(f => ({ ...f, is_unique: e.target.checked }))} />
                Mark as unique (no two records in this module can share the same value)
              </label>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={createField} disabled={savingField} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {savingField ? "Creating…" : "Create field"}
              </Button>
              <Button onClick={() => setShowFieldModal(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {showRecordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 overflow-y-auto py-8">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">{editingRecord ? "Edit record" : "New record"}</h2>
              <button onClick={() => setShowRecordModal(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Title *</label>
                <input value={recordTitle} onChange={e => setRecordTitle(e.target.value)}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              {activeFields.map(f => (
                <div key={f.field_id}>
                  <label className="text-xs text-[#7d8590] mb-1 block">{f.label}{f.is_required ? " *" : ""}</label>
                  {valueInput(f, recordValues[f.field_id], v => setRecordValues(vals => ({ ...vals, [f.field_id]: v })))}
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={saveRecord} disabled={savingRecord} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {savingRecord ? "Saving…" : editingRecord ? "Save changes" : "Create record"}
              </Button>
              <Button onClick={() => setShowRecordModal(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div></DarkPage>
  );
}
